import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import Seo, { SITE_URL } from "@/components/Seo";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, BedDouble, DoorOpen, Bath, Wifi, Car, MapPin, Clock, CalendarDays, Minus, Plus, Check, LogIn, LogOut, X, ChevronLeft, ChevronRight, Expand, Train, Bus, Utensils, Landmark, Trees, ShoppingBag, Star, Phone, Mail } from "lucide-react";
import { format } from "date-fns";
import { fr as dfr, enUS } from "date-fns/locale";
import PropertyMap from "@/components/PropertyMap";
import { useEcCalendar, ecDisabledMatcher, useEcQuote, EcQuoteBox } from "@/components/EcBooking";
import { ecBook } from "@/lib/easyConcierge";


// Pick a category icon for a "point of interest" string (heuristic on French keywords).
const poiIcon = (s) => {
  const t = (s || "").toLowerCase();
  if (/(gare|train|sncf|\bter\b|ferrovi)/.test(t)) return Train;
  if (/(bus|arr[êe]t|tram|m[ée]tro|astuce|t[ée]or|navette)/.test(t)) return Bus;
  if (/(resto|restaurant|bistrot|brasserie|caf[ée]|\bbar\b|table|gastronom)/.test(t)) return Utensils;
  if (/(cath[ée]drale|[ée]glise|mus[ée]e|monument|historial|abbaye|ch[âa]teau|palais|beffroi|gros-horloge|a[îi]tre|clo[îi]tre|jeanne|tour\b|panorama)/.test(t)) return Landmark;
  if (/(parc|jardin|square|quai|seine|for[êe]t|bois|promenade)/.test(t)) return Trees;
  if (/(march[ée]|commerce|supermarch[ée]|boulangerie|pharmacie|boutique|centre commercial|[ée]picerie)/.test(t)) return ShoppingBag;
  return MapPin;
};

// Étoiles (note réelle) — remplies selon la valeur.
const Stars = ({ value = 0, sm }) => {
  const sz = sm ? "w-3.5 h-3.5" : "w-4 h-4";
  return (
    <span className="inline-flex items-center gap-0.5 text-nj-gold" aria-label={`${value} sur 5`}>
      {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`${sz} ${i <= Math.round(value) ? "fill-current" : "text-nj-olive"}`} />)}
    </span>
  );
};

export default function PropertyDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const loc = lang === "fr" ? dfr : enUS;
  const [p, setP] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [guests, setGuests] = useState(2);
  const [reqOpen, setReqOpen] = useState(false);
  const [req, setReq] = useState({ first_name: "", last_name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [gallery, setGallery] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  const photos = p?.photos || [];
  const openLightbox = (i) => setLightbox(i);
  const closeLightbox = () => setLightbox(null);
  const nextPhoto = () => setLightbox((i) => (i + 1) % photos.length);
  const prevPhoto = () => setLightbox((i) => (i - 1 + photos.length) % photos.length);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, photos.length]);

  useEffect(() => {
    api.get(`/properties/${slug}`).then((r) => { if (r.data) setP(r.data); else setLoadFailed(true); }).catch(() => setLoadFailed(true));
    api.get(`/properties/${slug}/map`).then((r) => setMapData(r.data)).catch(() => {});
  }, [slug]);

  const disabledDays = [{ before: new Date() }];
  const ec = useEcCalendar(p?.easyconcierge_id);
  const ecQ = useEcQuote(p?.easyconcierge_id, ec.active, range, guests);
  if (ec.active) disabledDays.push(ecDisabledMatcher(ec.days, range));
  const ecIsRequest = /email|request|demande/i.test(ec.info?.booking_mode || "");

  const submitEc = async () => {
    const ci = format(range.from, "yyyy-MM-dd"), co = format(range.to, "yyyy-MM-dd");
    const base = {
      property_id: p.easyconcierge_id, check_in: ci, check_out: co, guests,
      guest_name: `${req.first_name} ${req.last_name}`.trim(), guest_email: req.email,
      guest_phone: req.phone, guest_message: req.message || undefined,
      due_lines: ecQ.quote?.deposit?.due_lines,
    };
    const origin = window.location.origin;
    let r = await ecBook({ ...base, return_url: `${origin}/reservation/confirmee`, cancel_url: window.location.href });
    if (!r.success && /retour|return|domaine|url/i.test(r.error || "")) r = await ecBook(base);
    if (r.httpStatus === 409 || r.reload) { toast.error("Le montant a été mis à jour, merci de vérifier le nouveau prix."); ecQ.refresh(); setReqOpen(false); return; }
    if (r.httpStatus === 429) { toast.error("Trop de demandes en cours, merci de réessayer dans quelques minutes."); return; }
    if (!r.success) { toast.error(r.error || (r.errors || []).join(" · ") || "Réservation impossible, réessayez"); return; }
    if (r.stripe_url) { window.location.href = r.stripe_url; return; }
    const qs = new URLSearchParams({ mode: r.mode || "", public_booking_id: r.public_booking_id || "", guest_email: req.email });
    nav(`/reservation/confirmee?${qs.toString()}`);
  };

  const submitRequest = async () => {
    if (!req.first_name || !req.last_name || !req.email || !req.phone) { toast.error("Merci d'indiquer nom, email et téléphone"); return; }
    setSubmitting(true);
    if (ec.active) {
      try { await submitEc(); } catch { toast.error("Service de réservation indisponible, réessayez"); }
      finally { setSubmitting(false); }
      return;
    }
    try {
      await api.post("/booking-request", {
        property_slug: slug, property_name: p?.name,
        checkin: format(range.from, "yyyy-MM-dd"), checkout: format(range.to, "yyyy-MM-dd"),
        guests, first_name: req.first_name, last_name: req.last_name,
        email: req.email, phone: req.phone, message: req.message || null,
      });
      toast.success("Demande envoyée ! Nous vous recontactons très vite.");
      setReqOpen(false);
      setReq({ first_name: "", last_name: "", email: "", phone: "", message: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible, réessayez"); }
    finally { setSubmitting(false); }
  };

  if (loadFailed) return <Layout><div className="max-w-3xl mx-auto px-4 py-24 text-center text-nj-muted" data-testid="property-not-found">Ce logement est introuvable ou n'est plus disponible.</div></Layout>;
  if (!p) return <Layout><div className="max-w-3xl mx-auto px-4 py-24 text-nj-muted">{t("common.loading")}</div></Layout>;

  const specs = [
    { icon: Users, label: `${p.capacity} ${t("common.guests")}` },
    { icon: BedDouble, label: `${p.bedrooms} ${t("common.bedrooms")}` },
    { icon: DoorOpen, label: `${p.beds} ${t("common.beds")}` },
    { icon: Bath, label: `${p.bathrooms} ${t("common.bathrooms")}` },
  ];

  return (
    <Layout>
      <Seo
        title={`${p.name} à ${p.city || "Rouen"} | Location courte durée | NJ Conciergerie`}
        description={(p.short_description || p.description || "").slice(0, 155) || `Réservez ${p.name} à Rouen en direct. ${p.capacity} voyageurs, ${p.bedrooms} chambres.`}
        canonical={`/logements/${slug}`}
        image={p.photos?.[0]}
        jsonLd={[
          {
            "@context": "https://schema.org", "@type": "Apartment", name: p.name,
            description: p.short_description || p.description,
            numberOfBedrooms: p.bedrooms, occupancy: { "@type": "QuantitativeValue", maxValue: p.capacity },
            image: p.photos?.slice(0, 6), url: `${SITE_URL}/logements/${slug}`,
            address: { "@type": "PostalAddress", addressLocality: p.city || "Rouen", addressRegion: "Normandie", addressCountry: "FR" },
            ...(p.rating ? {
              aggregateRating: { "@type": "AggregateRating", ratingValue: p.rating.value, reviewCount: p.rating.count, bestRating: 5 },
              review: (p.reviews || []).slice(0, 10).map((r) => ({
                "@type": "Review",
                reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
                author: { "@type": "Person", name: r.author_name },
                datePublished: r.date,
                reviewBody: r.comment,
              })),
            } : {}),
          },
        ]} />
      {/* Gallery */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="relative grid gap-2 md:grid-cols-4 md:grid-rows-2 rounded-lg overflow-hidden md:h-[62vh]">
          <button onClick={() => openLightbox(0)} data-testid="gallery-main" className="md:col-span-2 md:row-span-2 overflow-hidden bg-nj-olive group">
            <img src={p.photos?.[0]} alt={p.name} className="w-full h-64 md:h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          </button>
          {p.photos?.slice(1, 5).map((ph, i) => (
            <button key={ph} onClick={() => openLightbox(i + 1)} data-testid={`gallery-thumb-${i}`} className="overflow-hidden bg-nj-olive hidden md:block group">
              <img src={ph} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            </button>
          ))}
          {photos.length > 1 && (
            <button onClick={() => openLightbox(0)} data-testid="gallery-open-all"
              className="absolute bottom-4 right-4 flex items-center gap-2 bg-white/95 backdrop-blur text-nj-text text-sm font-medium px-4 py-2 rounded-sm shadow-md hover:bg-white transition-colors">
              <Expand className="w-4 h-4" /> Voir les {photos.length} photos
            </button>
          )}
        </div>
      </div>

      {lightbox !== null && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" data-testid="lightbox" onClick={closeLightbox}>
          <button data-testid="lightbox-close" className="absolute top-5 right-5 text-white/80 hover:text-white p-2" onClick={closeLightbox}><X className="w-7 h-7" /></button>
          <span className="absolute top-6 left-6 text-white/70 text-sm">{lightbox + 1} / {photos.length}</span>
          {photos.length > 1 && (
            <button data-testid="lightbox-prev" className="absolute left-4 md:left-8 text-white/70 hover:text-white p-3" onClick={(e) => { e.stopPropagation(); prevPhoto(); }}><ChevronLeft className="w-9 h-9" /></button>
          )}
          <img src={photos[lightbox]} alt="" className="max-h-[85vh] max-w-[90vw] object-contain nj-fade-up" onClick={(e) => e.stopPropagation()} />
          {photos.length > 1 && (
            <button data-testid="lightbox-next" className="absolute right-4 md:right-8 text-white/70 hover:text-white p-3" onClick={(e) => { e.stopPropagation(); nextPhoto(); }}><ChevronRight className="w-9 h-9" /></button>
          )}
          <div className="absolute bottom-5 inset-x-0 flex justify-center gap-2 px-4 overflow-x-auto no-scrollbar" onClick={(e) => e.stopPropagation()}>
            {photos.map((ph, i) => (
              <button key={ph} onClick={() => setLightbox(i)} className={`h-14 w-20 rounded overflow-hidden shrink-0 border-2 transition-all ${i === lightbox ? "border-white" : "border-transparent opacity-60 hover:opacity-100"}`}>
                <img src={ph} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-3 gap-12">
        {/* Left */}
        <div className="lg:col-span-2">
          <span className="flex items-center gap-1.5 text-sm text-nj-muted"><MapPin className="w-4 h-4" /> {p.district}, {p.city}</span>
          <h1 className="font-serif font-light text-4xl sm:text-5xl text-nj-text mt-2">{p.name}</h1>
          <div className="flex flex-wrap gap-5 mt-5 pb-8 border-b border-nj-olive">
            {specs.map((s) => (
              <span key={s.label} className="flex items-center gap-2 text-nj-text2"><s.icon className="w-5 h-5 text-nj-primary" /> {s.label}</span>
            ))}
          </div>

          <p className="text-nj-text2 leading-relaxed mt-8 whitespace-pre-line">{p.description}</p>

          {/* Times */}
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            <div className="bg-nj-olive/50 rounded-md p-5 flex items-start gap-3">
              <LogIn className="w-5 h-5 text-nj-primary mt-0.5" />
              <div><div className="font-semibold text-nj-text">{t("common.checkin")}</div><div className="text-sm text-nj-text2">{t("times.checkinInfo")}</div></div>
            </div>
            <div className="bg-nj-olive/50 rounded-md p-5 flex items-start gap-3">
              <LogOut className="w-5 h-5 text-nj-primary mt-0.5" />
              <div><div className="font-semibold text-nj-text">{t("common.checkout")}</div><div className="text-sm text-nj-text2">{t("times.checkoutInfo")}</div></div>
            </div>
          </div>

          {/* Amenities */}
          <h2 className="font-serif text-2xl text-nj-text mt-10 mb-4">Équipements</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {p.amenities?.map((a) => (
              <span key={a} className="flex items-center gap-2.5 text-nj-text2"><Check className="w-4 h-4 text-nj-primary" /> {a}</span>
            ))}
            {p.wifi && <span className="flex items-center gap-2.5 text-nj-text2"><Wifi className="w-4 h-4 text-nj-primary" /> Wi-Fi</span>}
            {p.parking && <span className="flex items-center gap-2.5 text-nj-text2"><Car className="w-4 h-4 text-nj-primary" /> {p.parking}</span>}
          </div>

          {/* House rules */}
          {p.house_rules && (<>
            <h2 className="font-serif text-2xl text-nj-text mt-10 mb-3">Règlement intérieur</h2>
            <p className="text-nj-text2 leading-relaxed">{p.house_rules}</p>
          </>)}

          {/* Avis clients (vérifiés) */}
          {p.reviews?.length > 0 && (<>
            <h2 className="font-serif text-2xl text-nj-text mt-10 mb-1">Avis clients</h2>
            <div className="flex items-center gap-2 mb-4" data-testid="reviews-summary">
              <Stars value={p.rating?.value || 0} />
              <span className="font-semibold text-nj-text">{p.rating?.value}</span>
              <span className="text-sm text-nj-muted">· {p.rating?.count} avis vérifié{p.rating?.count > 1 ? "s" : ""}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4" data-testid="reviews-list">
              {p.reviews.map((r) => (
                <div key={r.id} className="bg-nj-olive/40 rounded-md p-4" data-testid="review-card">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-nj-text">{r.author_name}</span>
                    <Stars value={r.rating} sm />
                  </div>
                  {r.comment && <p className="text-sm text-nj-text2 mt-2 leading-relaxed">{r.comment}</p>}
                  <div className="text-xs text-nj-muted mt-2">{r.source} · {r.date}</div>
                </div>
              ))}
            </div>
          </>)}

          {/* POI */}
          {p.points_of_interest?.length > 0 && (<>
            <h2 className="font-serif text-2xl text-nj-text mt-10 mb-4">À proximité</h2>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="poi-nearby">
              {p.points_of_interest.map((poi) => {
                const Icon = poiIcon(poi);
                return (
                  <div key={poi} className="flex items-center gap-3 bg-nj-olive/40 rounded-md px-3.5 py-2.5">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white text-nj-primary shrink-0"><Icon className="w-4 h-4" /></span>
                    <span className="text-nj-text2 text-sm">{poi}</span>
                  </div>
                );
              })}
            </div>
          </>)}

          {/* Map */}
          <h2 className="font-serif text-2xl text-nj-text mt-10 mb-4">Localisation</h2>
          <div className="rounded-md overflow-hidden border border-nj-olive aspect-[16/9]">
            {mapData?.center ? (
              <PropertyMap center={mapData.center} points={mapData.points} address={mapData.address} />
            ) : (
              <iframe title="carte" width="100%" height="100%" style={{ border: 0 }} loading="lazy"
                src={`https://www.google.com/maps?q=${encodeURIComponent(p.address || `${p.district || ""}, ${p.city || "Rouen"}`)}&z=15&output=embed`} />
            )}
          </div>
          {mapData?.points?.length > 0 && (
            <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3 text-xs text-nj-muted" data-testid="map-legend">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full inline-block" style={{ background: "#1C3F3A" }} />Le logement</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full inline-block" style={{ background: "#2f7dd1" }} />Transports</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full inline-block" style={{ background: "#c9a24b" }} />Monuments</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full inline-block" style={{ background: "#777777" }} />Autres lieux</span>
            </div>
          )}
        </div>

        {/* Right — booking module (sticky) */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-28 bg-white border border-nj-olive rounded-lg p-6 shadow-sm" data-testid="reserve-module">
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-semibold text-nj-text">{p.price_from ?? p.pricing?.base_price}€</span>
              <span className="text-sm text-nj-muted">/ {t("common.night")}</span>
            </div>

            <div className="mt-5 border border-nj-olive rounded-md divide-y divide-nj-olive">
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="detail-dates" className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-nj-olive/30">
                    <CalendarDays className="w-5 h-5 text-nj-primary" />
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-nj-muted font-semibold">{t("common.checkin")} — {t("common.checkout")}</div>
                      <div className="text-sm font-medium text-nj-text">
                        {range.from ? `${format(range.from, "dd MMM", { locale: loc })} ${range.to ? "→ " + format(range.to, "dd MMM", { locale: loc }) : ""}` : "Sélectionner"}
                      </div>
                    </div>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar mode="range" selected={range} onSelect={(r) => setRange(r || {})} onMonthChange={ec.loadMonth} numberOfMonths={1} disabled={disabledDays} locale={loc} />
                </PopoverContent>
              </Popover>
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-nj-primary" />
                  <div><div className="text-[10px] uppercase tracking-wider text-nj-muted font-semibold">{t("common.guests")}</div><div className="text-sm font-medium">{guests}</div></div>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid="detail-guests-minus" onClick={() => setGuests(Math.max(1, guests - 1))} className="w-8 h-8 rounded-full border border-nj-olive flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                  <span className="w-5 text-center font-semibold">{guests}</span>
                  <button data-testid="detail-guests-plus" onClick={() => setGuests(Math.min(p.capacity, guests + 1))} className="w-8 h-8 rounded-full border border-nj-olive flex items-center justify-center"><Plus className="w-4 h-4" /></button>
                </div>
              </div>
            </div>

            <div className="mt-4 text-xs text-nj-text2 bg-nj-olive/30 border border-nj-olive rounded-md p-3" data-testid="cancellation-policy">
              <strong className="text-nj-text">Conditions d'annulation :</strong> annulation gratuite jusqu'à 15 jours avant l'arrivée · 50 % remboursé entre 7 et 15 jours · aucun remboursement à moins de 7 jours de l'arrivée.
            </div>

            {ec.active && <EcQuoteBox quote={ecQ.quote} error={ecQ.error} loading={ecQ.loading} />}

            <button data-testid="detail-request-btn" onClick={() => setReqOpen(true)}
              disabled={!range.from || !range.to || ec.loading || (ec.active && !ecQ.quote)}
              className="w-full mt-5 bg-nj-primary text-white font-semibold py-3.5 rounded-sm hover:bg-nj-primaryHover disabled:opacity-40 transition-colors">
              {ec.active ? (ecIsRequest ? "Demander cette réservation" : "Réserver") : "Demander cette réservation"}
            </button>
            <p className="mt-3 text-center text-xs text-nj-muted" data-testid="detail-request-hint">
              {ec.active ? (ecIsRequest ? "Disponibilités en temps réel · confirmation par nos soins" : "Disponibilités en temps réel · paiement sécurisé") : "Réponse rapide · sans engagement · sans paiement en ligne"}
            </p>
            <div className="mt-4 space-y-2 text-sm">
              <a href="tel:+33666934267" data-testid="detail-call" className="flex items-center justify-center gap-2 border border-nj-olive rounded-sm py-2.5 font-medium text-nj-primary hover:bg-nj-olive/30 transition-colors"><Phone className="w-4 h-4" /> 06 66 93 42 67</a>
              <a href="mailto:contact@laconciergerierouen.com" data-testid="detail-email" className="flex items-center justify-center gap-2 border border-nj-olive rounded-sm py-2.5 font-medium text-nj-primary hover:bg-nj-olive/30 transition-colors"><Mail className="w-4 h-4" /> Nous écrire</a>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-nj-muted"><Clock className="w-3.5 h-3.5" /> {t("times.checkinInfo")} · {t("times.checkoutInfo")}</div>
          </div>
        </div>
      </div>

      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent data-testid="booking-request-dialog" className="max-w-md">
          <DialogHeader><DialogTitle>{ec.active && !ecIsRequest ? "Finaliser ma réservation" : "Demande de réservation"}</DialogTitle><DialogDescription>{ec.active && !ecIsRequest ? "Indiquez vos coordonnées, vous serez ensuite redirigé vers le paiement sécurisé." : "Indiquez vos coordonnées, nous vous recontactons rapidement pour confirmer les disponibilités."}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm bg-nj-olive/30 border border-nj-olive rounded-md p-3">
              <div className="font-medium text-nj-text">{p?.name}</div>
              <div className="text-nj-text2 mt-1">
                {range.from ? format(range.from, "dd MMM", { locale: loc }) : "—"} → {range.to ? format(range.to, "dd MMM yyyy", { locale: loc }) : "—"} · {guests} voyageur(s)
              </div>
              {ec.active && ecQ.quote && (
                <div className="text-nj-text font-semibold mt-1" data-testid="req-ec-amount">
                  Total {ecQ.quote.breakdown?.total}€{ecQ.quote.deposit?.is_partial ? ` · à régler maintenant ${ecQ.quote.deposit.amount}€` : ""}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prénom *</Label><Input data-testid="req-firstname" value={req.first_name} onChange={(e) => setReq({ ...req, first_name: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Nom *</Label><Input data-testid="req-lastname" value={req.last_name} onChange={(e) => setReq({ ...req, last_name: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Email *</Label><Input type="email" data-testid="req-email" value={req.email} onChange={(e) => setReq({ ...req, email: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Téléphone *</Label><Input data-testid="req-phone" value={req.phone} onChange={(e) => setReq({ ...req, phone: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Votre message / demandes</Label><Textarea data-testid="req-message" rows={3} value={req.message} onChange={(e) => setReq({ ...req, message: e.target.value })} placeholder="Précisions, questions, demandes particulières…" className="mt-1.5" /></div>
            <p className="text-xs text-nj-muted">Vous préférez le téléphone ? Appelez-nous au <strong>06 66 93 42 67</strong>.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReqOpen(false)} type="button">Annuler</Button>
            <Button onClick={submitRequest} disabled={submitting} data-testid="req-submit" className="bg-nj-primary text-white rounded-sm">{submitting ? "Envoi…" : (ec.active && !ecIsRequest ? "Continuer vers le paiement" : "Envoyer ma demande")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
